import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

import { fCurrency } from '../../../utils/formatNumber';

const styles = StyleSheet.create({
  page: { padding: 30, fontSize: 10, fontFamily: 'Helvetica' },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    paddingBottom: 10,
  },
  headerTextContainer: { flex: 1 },
  logo: { width: 120, maxHeight: 60, marginLeft: 20 },
  title: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: '#1e293b', marginBottom: 4 },
  subtitle: { fontSize: 10, color: '#64748b' },
  table: { display: 'flex', flexDirection: 'column', width: 'auto', marginTop: 10, borderStyle: 'solid', borderWidth: 1, borderColor: '#e2e8f0' },
  tableHeader: { flexDirection: 'row', backgroundColor: '#f8fafc', borderBottomWidth: 1, borderBottomColor: '#e2e8f0', paddingVertical: 6, paddingHorizontal: 4 },
  tableRow: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#e2e8f0', paddingVertical: 6, paddingHorizontal: 4 },
  colId: { width: '5%', textAlign: 'center' },
  colItem: { width: '16%' },
  colLoc: { width: '9%' },
  colProv: { width: '9%' },
  colTotalInv: { width: '6%', textAlign: 'center' },
  colLowStock: { width: '6%', textAlign: 'center' },
  colComp: { width: '6%', textAlign: 'center' },
  colLibre: { width: '6%', textAlign: 'center' },
  colBuy: { width: '7%', textAlign: 'center' },
  colPrice: { width: '13%', textAlign: 'right' },
  colTotalBuy: { width: '17%', textAlign: 'right' },
  colHeader: { fontFamily: 'Helvetica-Bold', fontSize: 9, color: '#475569' },
  rowText: { fontSize: 9, color: '#334155' },
  rowTextBold: { fontSize: 9, color: '#334155', fontFamily: 'Helvetica-Bold' },
  rowTextRed: { fontSize: 9, color: '#e11d48', fontFamily: 'Helvetica-Bold' },
  summaryContainer: { marginTop: 20, flexDirection: 'row', justifyContent: 'space-between', borderTopWidth: 2, borderTopColor: '#e2e8f0', paddingTop: 10 },
  summaryBox: { width: '23%', padding: 10, backgroundColor: '#f8fafc', borderRadius: 4, borderWidth: 1, borderColor: '#e2e8f0' },
  summaryLabel: { fontSize: 9, color: '#64748b', marginBottom: 4, fontFamily: 'Helvetica-Bold' },
  summaryValue: { fontSize: 12, color: '#1e293b', fontFamily: 'Helvetica-Bold' },
  summaryValueRed: { fontSize: 12, color: '#e11d48', fontFamily: 'Helvetica-Bold' },
});

export default function InventoryComparisonPdf({
  data,
  categories,
  summary,
  projectName,
  projectObj,
  isGrouped,
  projectsData,
  freeData
}) {
  if (!isGrouped && !data) return null;

  const renderPage = (itemsList, sectionTitle, sectProjectObj, sectSummary, isFreeSection = false) => {
    return (
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.headerContainer}>
          <View style={styles.headerTextContainer}>
            <Text style={styles.title}>Reporte de Comparativa de Inventario</Text>
            {sectProjectObj ? (
              <View style={{ marginBottom: 6, marginTop: 4 }}>
                <Text style={[styles.subtitle, { fontFamily: 'Helvetica-Bold', color: '#1e293b', fontSize: 11, marginBottom: 2 }]}>
                  Proyecto: {sectProjectObj.id}
                </Text>
                <Text style={[styles.subtitle, { color: '#334155', fontSize: 10, marginBottom: 2 }]}>
                  <Text style={{ fontFamily: 'Helvetica-Bold' }}>Cliente: </Text>{sectProjectObj.customerName || sectProjectObj.customer || 'S/N'}
                </Text>
                <Text style={[styles.subtitle, { color: '#334155', fontSize: 10, marginBottom: 2 }]}>
                  <Text style={{ fontFamily: 'Helvetica-Bold' }}>Sistema Motriz: </Text>{sectProjectObj.elevatorTypeName || 'S/N'}
                </Text>
                <Text style={[styles.subtitle, { color: '#334155', fontSize: 10, marginBottom: 2 }]}>
                  <Text style={{ fontFamily: 'Helvetica-Bold' }}>Tipo de Ascensor: </Text>{sectProjectObj.typeDriveSystemName || 'S/N'}
                </Text>
              </View>
            ) : sectionTitle ? (
              <Text style={[styles.subtitle, { fontFamily: 'Helvetica-Bold', color: '#1e293b', fontSize: 11, marginBottom: 4, marginTop: 4 }]}>
                {sectionTitle}
              </Text>
            ) : null}
            <Text style={styles.subtitle}>Stock total, comprometido en proyectos, libre y déficit de compras.</Text>
            <Text style={styles.subtitle}>Generado el {new Date().toLocaleDateString()}</Text>
          </View>
          <Image style={styles.logo} src="/img/logo.png" />
        </View>

        {sectSummary && (
          <View style={styles.summaryContainer}>
            <View style={styles.summaryBox}>
              <Text style={styles.summaryLabel}>TOTAL ÍTEMS</Text>
              <Text style={styles.summaryValue}>{sectSummary.totalItems}</Text>
            </View>
            <View style={styles.summaryBox}>
              <Text style={styles.summaryLabel}>COMPROMETIDO</Text>
              <Text style={styles.summaryValue}>{sectSummary.committed} unds.</Text>
            </View>
            <View style={styles.summaryBox}>
              <Text style={styles.summaryLabel}>TOTAL DISP. LIBRE</Text>
              <Text style={styles.summaryValue}>{sectSummary.available} unds.</Text>
            </View>
            <View style={styles.summaryBox}>
              <Text style={styles.summaryLabel}>TOTAL A COMPRAR</Text>
              <Text style={styles.summaryValueRed}>{Math.round(sectSummary.toBuyUnits)} unds.</Text>
              <Text style={styles.summaryValueRed}>{fCurrency(sectSummary.toBuyCost)}</Text>
            </View>
          </View>
        )}

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.colId, styles.colHeader]}>ID</Text>
            <Text style={[styles.colItem, styles.colHeader]}>ÍTEM</Text>
            <Text style={[styles.colLoc, styles.colHeader]}>UBIC.</Text>
            <Text style={[styles.colProv, styles.colHeader]}>PROV.</Text>
            <Text style={[styles.colTotalInv, styles.colHeader]}>TOTAL INV.</Text>
            <Text style={[styles.colLowStock, styles.colHeader]}>STK BAJO</Text>
            <Text style={[styles.colComp, styles.colHeader]}>COMPROMETIDO</Text>
            <Text style={[styles.colLibre, styles.colHeader]}>DISP. LIBRE</Text>
            <Text style={[styles.colBuy, styles.colHeader]}>A COMPRAR</Text>
            <Text style={[styles.colPrice, styles.colHeader]}>P. UNIT</Text>
            <Text style={[styles.colTotalBuy, styles.colHeader]}>SUMATORIA</Text>
          </View>

          {itemsList.map((row, index) => {
            const catObj = categories.find(c => String(c.id) === String(row.category));
            const catName = catObj ? catObj.description || catObj.name : 'SIN CATEGORÍA';
            
            const total = Number(Math.max(0, row.total_inventory).toFixed(2));
            const comp = isFreeSection ? 0 : Number(Math.max(0, row.separated_inventory).toFixed(2));
            const lib = Number(Math.max(0, row.available_inventory).toFixed(2));
            
            const deficit = row.deficit || 0;
            const isBuy = deficit > 0;

            return (
              <View key={index} style={styles.tableRow} wrap={false}>
                <Text style={[styles.colId, styles.rowText]}>{row.id}</Text>
                <View style={styles.colItem}>
                  <Text style={styles.rowTextBold}>{row.item_name}</Text>
                  <Text style={{ fontSize: 8, color: '#94a3b8' }}>{catName}</Text>
                </View>
                <Text style={[styles.colLoc, styles.rowText]}>{[row.position1, row.position2, row.position3].filter(Boolean).join('-') || '-'}</Text>
                <Text style={[styles.colProv, styles.rowText]}>{row.proveedor || '-'}</Text>
                <Text style={[styles.colTotalInv, styles.rowText]}>{total}</Text>
                <Text style={[styles.colLowStock, styles.rowText]}>{row.low_stock || 0}</Text>
                <Text style={[styles.colComp, styles.rowText]}>{comp > 0 ? comp : '—'}</Text>
                <Text style={[styles.colLibre, styles.rowText]}>{lib}</Text>
                <Text style={[styles.colBuy, isBuy ? styles.rowTextRed : styles.rowText]}>{deficit > 0 ? deficit : '—'}</Text>
                <Text style={[styles.colPrice, styles.rowText]}>{fCurrency(row.price || 0)}</Text>
                <Text style={[styles.colTotalBuy, isBuy ? styles.rowTextRed : styles.rowText]}>{deficit > 0 ? fCurrency(deficit * (row.price || 0)) : '—'}</Text>
              </View>
            );
          })}
        </View>
      </Page>
    );
  };

  return (
    <Document>
      {isGrouped ? (
        [
          ...(projectsData || []).map((proj, idx) =>
            renderPage(proj.items, null, proj.projectObj, proj.summary, false)
          ),
          ...(freeData && freeData.items && freeData.items.length > 0 ? [
            renderPage(freeData.items, "Proyecto: Ítems Libres (Sin Asignar)", null, freeData.summary, true)
          ] : [])
        ]
      ) : (
        renderPage(data, projectName ? `Proyecto: ${projectName}` : null, projectObj, summary, false)
      )}
    </Document>
  );
}
